// src/utils/expensePermissions.js
// Centralized permission helpers for expense-related pages.
// Reads from the existing RBAC model (admin / admin-pa / supervisor / user).
// Does NOT replace canAccessPage() in App.js — supplements it with
// fine-grained expense-specific checks.

/**
 * Normalize role to one of the known values; unknown → 'user'.
 */
function normalizeRole(role) {
  if (!role) return 'user';
  // admin-pa is treated as admin on the frontend
  if (role === 'admin-pa') return 'admin';
  if (['admin', 'supervisor', 'user'].includes(role)) return role;
  return 'user';
}

// ── Page-level access ──────────────────────────────────────────────────

/** Expense Overview: admin + admin-pa only (read-only cross-table view) */
export function canViewExpenseOverview(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

/** Project Expenses page: admin + supervisor (supervisors see only their projects) */
export function canViewProjectExpenses(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'supervisor';
}

/** Office Expenses page: admin only (existing rule) */
export function canViewOfficeExpenses(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

// ── Reimbursements ─────────────────────────────────────────────────────

/** All authenticated users can submit reimbursements */
export function canSubmitReimbursement(user) {
  return !!user;
}

/** Can this user see the "My Claims" tab? Always yes if authenticated. */
export function canViewOwnClaims(user) {
  return !!user;
}

/** Can this user see the "Approvals" tab? Admin only. */
export function canViewApprovals(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

/** Can this user approve/reject a specific reimbursement record? Admin only. */
export function canApproveReimbursement(user, record) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

/** Can this user see the "Payouts" tab? Admin only. */
export function canViewPayouts(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

/** Can this user mark a reimbursement as paid? Admin only. */
export function canMarkReimbursed(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

// ── Project Expenses actions ───────────────────────────────────────────

/** Can this user create project expenses? Admin + supervisor. */
export function canCreateProjectExpense(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'supervisor';
}

/** Can this user bill a project expense to a client? Admin only. */
export function canBillProjectExpense(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

// ── Import ─────────────────────────────────────────────────────────────

/** Can this user access the import expenses flow? Admin only. */
export function canImportExpenses(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}

// ── New page-level access (restructured pages) ──────────────────────────

/** Expense Claims page: all authenticated users */
export function canViewExpenseClaims(user) {
  return !!user;
}

/** Expense Administration page: admin + supervisor */
export function canViewExpenseAdmin(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin' || r === 'supervisor';
}

/** Expense Analytics page: admin only */
export function canViewExpenseAnalytics(user) {
  const r = normalizeRole(user?.role);
  return r === 'admin';
}
